/**
 * API client for fetching data from the Python API
 *
 * Uses server functions to proxy requests from browser to internal API.
 * This keeps the API private while allowing client-side data fetching.
 */

import { createServerFn } from "@tanstack/react-start";

import type { JobsCount } from "@/types/db";

const getInternalApiUrl = () => {
  // Try multiple ways to access env vars (Bun, Node, Nitro)
  const bunEnv = (globalThis as Record<string, unknown>).Bun as
    | { env: Record<string, string | undefined> }
    | undefined;
  const url =
    bunEnv?.env.NITRO_INTERNAL_API_URL ||
    bunEnv?.env.INTERNAL_API_URL ||
    process.env.NITRO_INTERNAL_API_URL ||
    process.env.INTERNAL_API_URL ||
    "http://localhost:8000";
  return url;
};

/**
 * Server function to fetch categories - runs on server, callable from client
 */
export const getCategories = createServerFn({ method: "GET" }).handler(
  async () => {
    const res = await fetch(`${getInternalApiUrl()}/categories`);
    if (!res.ok) {
      throw new Error(`Failed to fetch categories: ${res.statusText}`);
    }
    return res.json();
  },
);

/**
 * Server function to fetch paginated jobs with optional tag filtering
 */
export const getJobs = createServerFn({ method: "GET" })
  .inputValidator(
    (data: {
      limit?: number;
      offset?: number;
      tags?: string[];
      search?: string;
      country?: string;
      state?: string;
      sponsored?: boolean;
    }) => data,
  )
  .handler(async ({ data: options }) => {
    const params = new URLSearchParams();
    if (options?.limit) params.set("limit", String(options.limit));
    if (options?.offset) params.set("offset", String(options.offset));
    if (options?.tags) {
      options.tags.forEach((tag) => params.append("tags[]", tag));
    }
    if (options?.search) params.set("search", options.search);
    if (options?.country) params.set("country", options.country);
    if (options?.state) params.set("state", options.state);
    // Tri-state: omit = no filter, true = only sponsored, false = only
    // non-sponsored. Must send `false` explicitly so the backend can
    // distinguish it from an omitted param.
    if (options?.sponsored !== undefined) {
      params.set("sponsored", String(options.sponsored));
    }

    const url = `${getInternalApiUrl()}/jobs${params.toString() ? `?${params}` : ""}`;
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to fetch jobs: ${res.statusText}`);
    }
    return res.json();
  });

/**
 * Server function to fetch just the job-count total for a filter set.
 * Reads from the cached totals primitive — cheap enough to call on every
 * homepage render, unlike getJobs({limit:1}).
 */
export const getJobsCount = createServerFn({ method: "GET" })
  .inputValidator(
    (data: {
      tags?: string[];
      search?: string;
      country?: string;
      state?: string;
      sponsored?: boolean;
      createdAfter?: string;
    }) => data,
  )
  .handler(async ({ data: options }): Promise<JobsCount> => {
    const params = new URLSearchParams();
    if (options?.tags) {
      options.tags.forEach((tag) => params.append("tags[]", tag));
    }
    if (options?.search) params.set("search", options.search);
    if (options?.country) params.set("country", options.country);
    if (options?.state) params.set("state", options.state);
    if (options?.sponsored !== undefined) {
      params.set("sponsored", String(options.sponsored));
    }
    if (options?.createdAfter)
      params.set("created_after", options.createdAfter);

    const url = `${getInternalApiUrl()}/jobs/count${params.toString() ? `?${params}` : ""}`;
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to fetch jobs count: ${res.statusText}`);
    }
    return (await res.json()) as JobsCount;
  });

/**
 * Server function to fetch a single job by slug
 */
export const getJobBySlug = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data: slug }) => {
    const res = await fetch(`${getInternalApiUrl()}/jobs/${slug}`);
    if (res.status === 404) {
      return null;
    }
    if (!res.ok) {
      throw new Error(`Failed to fetch job: ${res.statusText}`);
    }
    return res.json();
  });

/**
 * Server function to fetch a single tag by slug
 */
export const getTagBySlug = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data: slug }) => {
    const res = await fetch(`${getInternalApiUrl()}/tags/${slug}`);
    if (res.status === 404) {
      return null;
    }
    if (!res.ok) {
      throw new Error(`Failed to fetch tag: ${res.statusText}`);
    }
    return res.json();
  });

/**
 * Server function to fetch pre-computed stats for a tag
 */
export const getTagStats = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data: slug }) => {
    const res = await fetch(`${getInternalApiUrl()}/tags/${slug}/stats`);
    if (res.status === 404) return null;
    if (!res.ok)
      throw new Error(`Failed to fetch tag stats: ${res.statusText}`);
    return res.json();
  });

/**
 * Server function to subscribe an email to the newsletter
 */
export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string; t: number }) => data)
  .handler(async ({ data }) => {
    const res = await fetch(`${getInternalApiUrl()}/newsletter/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: data.email, t: data.t }),
    });
    const body = await res.json();
    if (!res.ok) {
      let detail = "Something went wrong. Please try again.";
      if (typeof body.detail === "string") {
        detail = body.detail;
      } else if (Array.isArray(body.detail) && body.detail.length > 0) {
        const first = body.detail[0];
        if (first?.loc?.includes("email") && first?.type === "value_error") {
          detail = "Please enter a valid email address.";
        }
      }
      throw new Error(detail);
    }
    return body as { success: boolean; message: string };
  });

/**
 * Server function to fetch the newsletter subscriber count
 */
export const getSubscriberCount = createServerFn({ method: "GET" }).handler(
  async () => {
    const res = await fetch(`${getInternalApiUrl()}/newsletter/count`);
    if (!res.ok) return { count: 0, display: "0" };
    return res.json() as Promise<{ count: number; display: string }>;
  },
);

/**
 * Server function to confirm a newsletter subscription via token
 */
export const confirmNewsletterSubscription = createServerFn({ method: "GET" })
  .inputValidator((token: string) => token)
  .handler(async ({ data: token }) => {
    const res = await fetch(
      `${getInternalApiUrl()}/newsletter/confirm?token=${encodeURIComponent(token)}`,
    );
    return { confirmed: res.ok };
  });

/**
 * Server function to fetch locations (countries/states with job counts)
 */
export const getLocations = createServerFn({ method: "GET" }).handler(
  async () => {
    const res = await fetch(`${getInternalApiUrl()}/locations`);
    if (!res.ok) {
      throw new Error(`Failed to fetch locations: ${res.statusText}`);
    }
    return res.json();
  },
);

/**
 * Server function to read PostHog config from runtime env vars
 */
export const getPostHogConfig = createServerFn({ method: "GET" }).handler(
  async () => {
    const key = process.env.VITE_PUBLIC_POSTHOG_KEY || "";
    const host =
      process.env.VITE_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
    return { key, host };
  },
);
